import { FormControl, MenuItem, type SelectChangeEvent, type Theme, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { type Control, Controller, type FieldValues, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

import type { ExperienceMember } from "@/Dashboard/pages/ExperiencesV2/types";
import AccountInfoDBC from "@/Dashboard/pages/MyAccount/components/AccountInfoDBC";
import type { ExperienceSettingsFormValues } from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsModal";
import * as S from "@/VisualBuilder/components/ExperienceSettingsDialog/styled";
import { MEDIA_SIZE } from "@/constants";
import { useUrlParams } from "@/hooks";
import { currentExperienceSelector } from "@/store/v2/currentExperience/selectors";
import type { Phone, User } from "@/store/v2/currentUser/selectors";
import { membersSelector } from "@/store/v2/members/selectors";
import { organizationUsersSelector } from "@/store/v2/organizationUsers/organizationUsersSlice";
import { getMediaLink } from "@/utils/mediaLink";

const ExperienceSettingsContent = () => {
  const [dbcSectionHeight, setDbcSectionHeight] = useState(0);
  const dbcSectionRef = useRef<HTMLDivElement>();
  const location = useLocation();

  useEffect(() => {
    setDbcSectionHeight(dbcSectionRef.current?.clientHeight || 0);
  }, [dbcSectionRef.current?.clientHeight]);

  const theme = useTheme<Theme>();
  const isOrganizationsSection = location.pathname.startsWith("/organizations");

  const { experience } = useSelector(currentExperienceSelector);
  const { createdBy } = experience || {};

  const isCollection = experience?.userCollection === true;

  const { control, setValue, watch } = useFormContext<ExperienceSettingsFormValues>();

  const values = watch();

  const { propertyId, organizationId } = useUrlParams();
  const { members: experienceMembers } = useSelector(membersSelector(propertyId)) as { members: ExperienceMember[] };
  const { data: organizationUsers } = useSelector(organizationUsersSelector(Number(organizationId)));

  const experienceUsers = (isOrganizationsSection ? organizationUsers : experienceMembers).map(
    (member: { user: User }) => member.user,
  );

  let createdByMember: User | undefined;
  let memberOptions: User[];

  if (createdBy) {
    createdByMember = experienceUsers.find((member: { id: number }) => member.id === createdBy.id);
    memberOptions = createdByMember === undefined ? [createdBy, ...experienceUsers] : experienceUsers;
  } else {
    memberOptions = experienceUsers;
  }

  const currentDbcUser = memberOptions.find((member: { id: number }) => member.id === values.dbcUserId);

  const photo = getMediaLink({ size: MEDIA_SIZE.small, link: currentDbcUser?.photoUrl });
  const [createdByPhone = {} as Phone] = currentDbcUser?.phones || [];

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    const numericValue = Number(e.target.value);
    setValue("dbcUserId", numericValue, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const hasMemberOptions = memberOptions.length > 0;

  return (
    <S.DialogContent>
      <S.FormSection $matchDbcSize={values.dbcEnabled && !!currentDbcUser} $dbcSectionHeight={dbcSectionHeight}>
        <S.FormTextField
          $theme={theme}
          disabled={values.locked}
          fullWidth
          name="title"
          label="Experience Title"
          size="small"
          variant="filled"
          control={control as unknown as Control<FieldValues>}
          inputProps={{
            autoComplete: "none",
          }}
        />
        <Controller
          name="dbcEnabled"
          control={control}
          render={({ field }) => (
            <S.FormControlLabel
              $theme={theme}
              control={
                <S.FormSwitch
                  $theme={theme}
                  disabled={values.locked}
                  checked={field.value}
                  disableRipple
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                  }}
                />
              }
              label="Digital Business Card"
            />
          )}
        />
        <Controller
          name="dbcUserId"
          control={control}
          render={({ field, fieldState }) => {
            const { invalid, isDirty, isTouched } = fieldState;

            return (
              <FormControl fullWidth style={{ margin: "4px" }}>
                <S.FormSelectField
                  $theme={theme}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: "#000",
                        color: "#fff",
                      },
                    },
                  }}
                  disabled={isCollection || values.locked || !hasMemberOptions || !values.dbcEnabled}
                  variant="outlined"
                  fullWidth
                  margin="none"
                  renderValue={() => <>{currentDbcUser?.name}</>}
                  value={field.value}
                  error={(isDirty || isTouched) && invalid}
                  IconComponent={S.ArrowDown}
                  onChange={(e) => handleChange(e)}
                >
                  {hasMemberOptions ? (
                    memberOptions.map((option, index) => (
                      <MenuItem id={`menu-item-${index}`} key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem id={`menu-item-0`} value={experience?.createdBy?.id}>
                      {experience?.createdBy?.name}
                    </MenuItem>
                  )}
                </S.FormSelectField>
              </FormControl>
            );
          }}
        />
        <Controller
          name="propertyInfoCardEnabled"
          control={control}
          render={({ field }) => (
            <S.FormControlLabel
              $theme={theme}
              control={
                <S.FormSwitch
                  $theme={theme}
                  disabled={values.locked}
                  checked={field.value}
                  disableRipple
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                  }}
                />
              }
              label="Property info card"
            />
          )}
        />
        {isCollection && (
          <Controller
            name="userCollectionSync"
            control={control}
            render={({ field }) => (
              <S.FormControlLabel
                $theme={theme}
                className="bottomSwitch"
                control={
                  <S.FormSwitch
                    $theme={theme}
                    disabled={values.locked}
                    checked={field.value}
                    disableRipple
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                    }}
                  />
                }
                label="Sync with Asset Library"
              />
            )}
          />
        )}
      </S.FormSection>
      {values.dbcEnabled && currentDbcUser && (
        <S.DBCSection ref={dbcSectionRef}>
          <AccountInfoDBC
            photo={photo || ""}
            name={currentDbcUser?.name || ""}
            email={(currentDbcUser?.displayEmail && currentDbcUser?.email) || ""}
            phone={(currentDbcUser?.displayWorkPhone && createdByPhone?.number) || ""}
          />
        </S.DBCSection>
      )}
    </S.DialogContent>
  );
};

export default ExperienceSettingsContent;
